import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthService } from 'providers/auth.service';
import { LicenseRedirectService } from 'providers/license-redirect.service';
import { map, take } from 'rxjs/operators';
import { FeatureFlagService } from '../services/feature-flags';

@Component({
    selector: 'auth-callback',
    templateUrl: './auth-callback.component.html',
    styleUrls: ['./auth-callback.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class AuthCallbackComponent {
    loggingIn: boolean;
    loginFailed: boolean;
    loginFailedReason: string;
    isDarkMode = false;

    constructor(
        private route: ActivatedRoute,
        private auth: AuthService,
        private readonly cdRef: ChangeDetectorRef,
        private readonly licenseRedirectService: LicenseRedirectService,
        private featureFlagService: FeatureFlagService
    ) {
        // Check dark mode from feature flag service (works via localStorage, no auth needed)
        this.isDarkMode = this.featureFlagService.isEnabled('enableDarkMode');

        this.route.queryParams
            .pipe(
                map(({ code, redirectUrl }) => {
                    this.login(code, redirectUrl);
                }),
                take(1),
                untilDestroyed(this)
            )
            .subscribe();
    }

    private async login(code: string, redirectUrl: string) {
        if (!code) {
            this.loggingIn = false;
            this.loginFailed = true;
            this.loginFailedReason = `Missing authorization code`;
            return;
        }

        this.loggingIn = true;
        this.loginFailed = false;
        this.cdRef.markForCheck();

        try {
            await this.auth.loginWithCode(code);
            if (!this._checkRedirectUrl(redirectUrl)) {
                this.loginFailed = true;
                this.loginFailedReason = `Callback URL mismatch`;
                this.loggingIn = false;
                this.cdRef.markForCheck();
                return;
            } else {
                // Check for license-based redirect
                const licenseRedirectOccurred = await this.licenseRedirectService.checkAndRedirectBasedOnLicense();
                if (!licenseRedirectOccurred) {
                    window.location.href = redirectUrl || '/';
                }
            }
        } catch (e) {
            this.loginFailed = true;
            this.loginFailedReason = `${e}`;
            this.loggingIn = false;
        }
    }

    private _checkRedirectUrl(redirectUrl: string) {
        const uri = new URL(redirectUrl);
        return [
            'localhost:4200',
            'dev.kempower.io',
            'dev.chargeye.io',
            'test.kempower.io',
            'test.chargeye.io',
            'kempower.io',
            'chargeye.io',
            'beta.kempower.io',
            'test.chargeye.au',
            'chargeye.au',
            'mykempower.com',
            'dev.mykempower.com',
            'test.mykempower.com',
        ].includes(uri.host);
    }
}
